import { useRef, useState } from "react";
import { toast } from "sonner";
import { Save, RotateCcw } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { brandAsset } from "@/lib/brand";
import { LogoImg, FIT_DEFAULT } from "@/components/LogoImg";

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

function Slider({ label, value, min, max, step, onChange, testId }) {
  return (
    <label className="block text-xs text-slate-300">{label}
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="mt-1 w-full accent-[#B7F52A]" data-testid={testId} />
    </label>
  );
}

const KINDS = {
  logo: { title: "Cadrage du logo", url: "logo_url", fit: "logo_fit", w: 144, h: 144, round: "rounded-2xl", modes: true, ok: "Cadrage du logo enregistré" },
  banner: { title: "Cadrage de la photo d'équipe", url: "banner_url", fit: "banner_fit", w: 300, h: 100, round: "rounded-xl", modes: false, ok: "Cadrage de la photo enregistré" },
};

export default function ImageFitEditor({ lg, reload, kind = "logo" }) {
  const K = KINDS[kind];
  const b = lg.brand;
  const [fit, setFit] = useState({ ...FIT_DEFAULT, ...(b[K.fit] || {}) });
  const [busy, setBusy] = useState(false);
  const drag = useRef(null);
  const t = (k) => `${kind}-fit-${k}-${lg.id}`;
  const set = (k) => (v) => setFit((f) => ({ ...f, [k]: v }));

  const onDown = (e) => { if (fit.mode === "fill") { drag.current = { px: e.clientX, py: e.clientY, x: fit.x, y: fit.y }; e.currentTarget.setPointerCapture(e.pointerId); } };
  const onMove = (e) => {
    const d = drag.current;
    if (!d) return;
    setFit((f) => ({ ...f, x: clamp(d.x - (e.clientX - d.px) * 100 / (K.w * f.zoom), 0, 100), y: clamp(d.y - (e.clientY - d.py) * 100 / (K.h * f.zoom), 0, 100) }));
  };
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/superadmin/corporate-leagues/${lg.id}`, { [K.fit]: { ...fit, x: Math.round(fit.x), y: Math.round(fit.y) } }); toast.success(K.ok); reload(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  if (!b[K.url]) return null;
  const src = brandAsset(b[K.url]);
  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 p-4 sm:col-span-2" data-testid={t("editor")}>
      <p className="mb-3 text-sm font-bold text-white">{K.title}</p>
      <div className="flex flex-wrap items-start gap-5">
        <div className="max-w-full space-y-2">
          <div onPointerDown={onDown} onPointerMove={onMove} onPointerUp={() => { drag.current = null; }} onPointerCancel={() => { drag.current = null; }}
            className={`max-w-full touch-none ${K.round} ring-2 ring-[#B7F52A]/40 ${fit.mode === "fill" ? "cursor-grab active:cursor-grabbing" : ""}`} style={{ width: K.w, height: K.h }} data-testid={t("preview")}>
            <LogoImg src={src} fit={fit} alt="Aperçu" className={`h-full w-full ${K.round}`} />
          </div>
          {kind === "logo" && <div className="flex items-center gap-2 text-[11px] text-slate-400">
            <LogoImg src={src} fit={fit} alt="" className="h-9 w-9 shrink-0 rounded-xl" /> <LogoImg src={src} fit={fit} alt="" className="h-7 w-7 shrink-0 rounded-lg" /> Aperçu en petit
          </div>}
        </div>
        <div className="min-w-[200px] flex-1 space-y-3">
          {K.modes && <div className="flex flex-wrap gap-2">
            {[["fill", "Remplir l'espace"], ["fit", "Logo entier sur fond blanc"]].map(([k, l]) => (
              <button key={k} type="button" aria-pressed={fit.mode === k} onClick={() => set("mode")(k)} data-testid={t(`mode-${k}`)}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${fit.mode === k ? "border-[#B7F52A] bg-[#B7F52A]/15 text-[#B7F52A]" : "border-white/10 text-slate-300"}`}>{l}</button>
            ))}
          </div>}
          {fit.mode === "fill" ? (
            <>
              <Slider label="Zoom" value={fit.zoom} min={1} max={3} step={0.05} onChange={set("zoom")} testId={t("zoom")} />
              <Slider label="Gauche ↔ droite" value={fit.x} min={0} max={100} step={1} onChange={set("x")} testId={t("x")} />
              <Slider label="Haut ↕ bas" value={fit.y} min={0} max={100} step={1} onChange={set("y")} testId={t("y")} />
              <p className="text-[11px] text-slate-500">Astuce : fais glisser l'image dans l'aperçu. Si un réglage ne bouge rien, augmente un peu le zoom pour avoir de la marge.</p>
            </>
          ) : <p className="text-xs text-slate-400">Le logo est affiché en entier, centré sur un fond blanc.</p>}
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setFit(FIT_DEFAULT)} className="mpl-btn-ghost text-xs" data-testid={t("reset")}><RotateCcw size={13} /> Recentrer</button>
            <button type="button" onClick={save} disabled={busy} className="mpl-btn-primary text-xs" data-testid={t("save")}><Save size={13} /> Enregistrer le cadrage</button>
          </div>
        </div>
      </div>
    </div>
  );
}
